import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
} from "firebase/firestore";
import { firestore } from "../../../firebase";
// --- MUI Imports ---
import {
  Paper,
  Typography,
  IconButton,
  Box,
  Grid,
  Card,
} from "@mui/material";
// --- End MUI Imports ---
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import OrganisationAdminNavBar from "./OrganisationAdminNavBar";
import { getScheduleDueStats, toDate } from "../../../utils/amcScheduleStats";

const OUUserPage = () => {
  const navigate = useNavigate();

  // Stats State
  const [warrantyStats, setWarrantyStats] = useState({ count: 0, nextMonth: 0, today: 0 });
  const [amcStats, setAmcStats] = useState({ count: 0, nextMonth: 0, today: 0 });
  const [contractStats, setContractStats] = useState({ count: 0, nextMonth: 0, today: 0 });

  const auth = getAuth();

  const startOfToday = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const endOfToday = useMemo(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);
  const oneMonthFromNow = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const qUser = query(
            collection(firestore, "UserProfileMaster"),
            where("email", "==", user.email)
          );
          const userSnap = await getDocs(qUser);

          if (!userSnap.empty) {
            const profileData = userSnap.docs[0].data();
            const tenantId = profileData.tenantId;

            if (tenantId) {
              sessionStorage.setItem("tenantId", tenantId);
            }

            // 1. Warranty Stats
            const unsubWarranty = onSnapshot(
              query(collection(firestore, "ItemMaster"), where("userId", "==", user.uid)),
              (snap) => {
                let count = snap.size;
                let nextMonth = 0, today = 0;
                snap.forEach((doc) => {
                  const expiry = toDate(doc.data().warrantyExpiryDate);
                  if (!expiry) return;
                  if (expiry >= startOfToday && expiry <= endOfToday) today++;
                  if (expiry > endOfToday && expiry <= oneMonthFromNow) nextMonth++;
                });
                setWarrantyStats({ count, nextMonth, today });
              }
            );

            // 2. AMC Stats
            const unsubAmc = onSnapshot(
              query(collection(firestore, "AMCScheduleMaster"), where("createdBy", "==", user.email)),
              (snap) => {
                let count = snap.size;
                const { dueToday: today, dueNextMonth: nextMonth } =
                  getScheduleDueStats(
                    snap.docs.map((entry) => entry.data()),
                    startOfToday,
                    endOfToday,
                    oneMonthFromNow
                  );
                setAmcStats({ count, nextMonth, today });
              }
            );

            // 3. Contract Stats
            let unsubContracts = () => {};
            if (tenantId) {
              unsubContracts = onSnapshot(
                query(collection(firestore, "ContractMaster"), where("tenantId", "==", tenantId)),
                (snap) => {
                  let count = 0;
                  let nextMonth = 0, today = 0;
                  snap.forEach((doc) => {
                    const contract = doc.data();
                    if (contract.status !== "Active") return;
                    count += 1;
                    const expiry = toDate(contract.endDate);
                    if (!expiry) return;
                    if (expiry >= startOfToday && expiry <= endOfToday) today++;
                    if (expiry > endOfToday && expiry <= oneMonthFromNow) nextMonth++;
                  });
                  setContractStats({ count, nextMonth, today });
                }
              );
            } else {
              setContractStats({ count: 0, nextMonth: 0, today: 0 });
            }

            return () => {
              unsubWarranty();
              unsubAmc();
              unsubContracts();
            };
          }
        } catch (error) {
          console.error("Error loading OU User dashboard data:", error);
        }
      } else {
        navigate("/login");
      }
    });
    return () => unsubscribeAuth();
  }, [auth, navigate, startOfToday, endOfToday, oneMonthFromNow]);

  const Odometer = ({ label, value, color }) => (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        backgroundColor: "#fafafa",
        borderRadius: "8px",
        height: "120px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Typography variant="body2" sx={{ color: "text.secondary", m: 0, fontSize: "0.875rem" }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, m: 0, color: color, mt: 1 }}>
        {value}
      </Typography>
    </Paper>
  );

  const ActionCard = ({ title, text, navigateTo }) => (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: "12px",
      }}
    >
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {text}
        </Typography>
      </Box>
      <IconButton color="primary" onClick={() => navigate(navigateTo)}>
        <ArrowForwardIosIcon />
      </IconButton>
    </Paper>
  );

  return (
    <Box sx={{ bgcolor: "#f5f7fb", minHeight: "100vh" }}>
      <OrganisationAdminNavBar />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontFamily: "'Unbounded', cursive",
              fontWeight: 900,
              color: '#1c1400',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              lineHeight: 1,
            }}
          >
            Welcome to Aalay<span style={{ color: '#f59e0b', fontSize: '1.8rem' }}>.</span>
          </Typography>

          <Typography
            variant="caption"
            component="div"
            sx={{
              fontStyle: 'italic',
              color: '#1c1400',
              opacity: 0.85,
              fontSize: '0.85rem',
              mt: 0.5,
              fontFamily: 'inherit',
              textTransform: 'none',
            }}
          >
            Intelligent Operations by OmneVu.com
          </Typography>
        </Box>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Warranty Section */}
          <Grid item xs={12} sm={6} lg={4}>
            <Card sx={{ p: { xs: 2, md: 3 }, height: "100%", display: "flex", flexDirection: "column" }}>
              <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
                Warranty
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Odometer label="Total available" value={warrantyStats.count} color="#1976d2" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Odometer label="Expire in next month" value={warrantyStats.nextMonth} color="#EF6C00" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Odometer label="Targeted for today" value={warrantyStats.today} color="#D32F2F" />
                </Grid>
              </Grid>
              <Typography variant="h6" sx={{ fontWeight: "bold", my: 3 }}>
                My Warranty & Complaints
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flexGrow: 1 }}>
                <ActionCard title="Warranty Cards" text="View and manage your warranty items." navigateTo="/warranty_list" />
                <ActionCard title="Complaints" text="View or raise complaints for your products." navigateTo="/complaints_list" />
              </Box>
            </Card>
          </Grid>

          {/* AMC Section */}
          <Grid item xs={12} sm={6} lg={4}>
            <Card sx={{ p: { xs: 2, md: 3 }, height: "100%", display: "flex", flexDirection: "column" }}>
              <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
                AMC
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Odometer label="Total available" value={amcStats.count} color="#1976d2" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Odometer label="Expire in next month" value={amcStats.nextMonth} color="#EF6C00" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Odometer label="Targeted for today" value={amcStats.today} color="#D32F2F" />
                </Grid>
              </Grid>
              <Typography variant="h6" sx={{ fontWeight: "bold", my: 3 }}>
                AMC Management
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flexGrow: 1 }}>
                <ActionCard title="Create New AMC" text="Set up a new Annual Maintenance Contract." navigateTo="/amc/schedule/new" />
                <ActionCard title="Manage AMC Schedules" text="View, update, or track AMC schedules." navigateTo="/amc/schedule/list" />
              </Box>
            </Card>
          </Grid>

          {/* Contracts Section */}
          <Grid item xs={12} sm={6} lg={4}>
            <Card sx={{ p: { xs: 2, md: 3 }, height: "100%", display: "flex", flexDirection: "column" }}>
              <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
                Contracts
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Odometer label="Total active" value={contractStats.count} color="#1976d2" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Odometer label="Expire in next month" value={contractStats.nextMonth} color="#EF6C00" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Odometer label="Ending today" value={contractStats.today} color="#D32F2F" />
                </Grid>
              </Grid>
              <Typography variant="h6" sx={{ fontWeight: "bold", my: 3 }}>
                Contract Management
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flexGrow: 1 }}>
                <ActionCard title="Manage Contracts" text="View, update, or amend contracts." navigateTo="/contracts/list" />
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default OUUserPage;
